import { useEffect } from 'react';
import * as Sentry from '@sentry/react';

/**
 * Keeps THIS TAB out of Chromium's freezing intervention for as long as the
 * caller is mounted, so a feed socket in a background tab keeps receiving.
 *
 * The problem it solves: a hidden tab is frozen after a few minutes, and a
 * frozen page runs NO JavaScript. It therefore answers no engine.io PING, and
 * the gateway drops its socket at `pingTimeout`. The operator sees nothing
 * wrong — the tab is still open, the page still renders the rows it had — but
 * it stopped being a feed. Everything that happens while it is out arrives on
 * the next reconnect as a cursor REPLAY, and replayed frames are muted on
 * purpose (`QueuedNotification.live` in `useNotificationsSocket`). So a
 * backgrounded tab does not merely go quiet: it goes quiet and then hides
 * that it was quiet, because the rows all show up.
 *
 * The primitive is the same one `useSoundLeader` already relies on, and that
 * file is where the reasoning is written down: Chromium's freezing
 * intervention lists holding a Web Lock among the conditions under which a
 * page is NOT frozen, since freezing a lock holder would deadlock every tab
 * queued behind it. Today exactly ONE tab gets that exemption — the sound
 * leader — as a side effect of an election that exists for a different
 * reason. Every other tab with the feed open is unprotected. This hook gives
 * the exemption to each of them, on its own terms.
 *
 * 🚨 THE LOCK NAME IS UNIQUE PER TAB, AND THAT IS THE WHOLE DESIGN.
 *
 * A shared name would silently turn this into a second leader election: one
 * tab would hold it and every other tab would sit in the queue holding
 * nothing, which is the exact failure this hook exists to fix, wearing a new
 * hat. Uniqueness also means we never contend, never wait, and can never
 * delay a real lock — `useSoundLeader`'s election is untouched by anything
 * here, because these two locks share no name.
 *
 * 🚨 IT MUST NEVER BE RELEASED BECAUSE THE TAB WENT TO THE BACKGROUND, and
 * this hook must never listen to `visibilitychange`. Releasing on hide would
 * drop the exemption at the exact moment it is the only thing doing any work
 * — the same inversion that killed the 2026-05-23 sound leader. Eligibility
 * is the caller's business; visibility is not an input.
 *
 * No Web Locks (very old Safari, a locked-down enterprise wrapper): no-op.
 * The tab degrades to today's behaviour — it may freeze and reconnect — which
 * is what every tab does now. This hook can only ever add protection.
 *
 * ⚠️ KNOWN TRADE-OFF — bfcache. Holding a Web Lock continuously has
 * historically made a page ineligible for the back/forward cache in
 * Chromium, and this widens that from one tab (the sound leader, which
 * already held one) to every tab with the feed configured. The cost is a
 * slower restore when the operator navigates Back into the app; the thing
 * being bought is that the feed does not silently stop receiving. For an
 * operator console that sits open for hours that is the right side of the
 * trade, but it IS a trade and it should not be discovered later by someone
 * bisecting a "Back is slow now" report.
 */

/**
 * Prefix of the per-tab lock name. The suffix below makes it unique; the
 * prefix is here so the name is recognisable in a `navigator.locks.query()`
 * dump when someone is debugging a stuck tab.
 */
export const TAB_ALIVE_LOCK_PREFIX = 'feed-tab-alive';

type LockManagerLike = {
  request: (
    name: string,
    options: { mode: 'exclusive'; signal?: AbortSignal },
    callback: () => Promise<void>
  ) => Promise<unknown>;
};

/**
 * Read at effect time (not module load) so a test can install/remove it.
 *
 * No `typeof navigator === 'undefined'` guard: this runs in a `useEffect`, in
 * a Vite SPA with no SSR, so there is no render path without a `navigator` —
 * the guard would be unreachable, and the two other unreachable "defences" in
 * this file were deleted for exactly that reason. The shape check below is
 * the one that earns its keep: a wrapper or a partial polyfill can leave
 * `navigator.locks` present without a usable `request`.
 */
function getLockManager(): LockManagerLike | null {
  const locks = (navigator as Navigator & { locks?: LockManagerLike }).locks;
  return typeof locks?.request === 'function' ? locks : null;
}

/**
 * This tab's lock name, minted once per document.
 *
 * Stable across mount/unmount cycles so a remount reuses the name rather than
 * leaking a fresh one on every navigation; unique across tabs so two tabs
 * never contend. `randomUUID` is not universal over plain HTTP, so the
 * fallback keeps this working in a dev server on `http://` — it only has to
 * be unlikely to collide with the handful of tabs one operator has open.
 */
let tabLockName: string | null = null;
function lockNameForThisTab(): string {
  if (tabLockName) return tabLockName;
  const uuid =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  tabLockName = `${TAB_ALIVE_LOCK_PREFIX}-${uuid}`;
  return tabLockName;
}

/**
 * How many mounted callers want this tab kept awake.
 *
 * Module state, not per-hook state, because the thing being protected is the
 * DOCUMENT. Three components call `useNotificationsSocket` over one shared
 * socket (the sidebar bell, the sound mount, the feed page); they must hold
 * ONE lock between them, and the last one to leave is what releases it.
 */
let holders = 0;

/**
 * Bumped on every acquire and every release, so a grant that lands after the
 * caller already unmounted can tell it is stale and let go immediately
 * instead of pinning the lock for the life of the tab.
 */
let generation = 0;

let releaseCurrent: (() => void) | null = null;
let abortCurrent: AbortController | null = null;

/**
 * The lock manager refused, so we stop asking for the rest of the session.
 *
 * Stated rather than accidental. Every real cause is PERMANENT — an opaque
 * origin, an enterprise policy, a browser without the API behind a flag — so
 * retrying cannot make it start working, and without this flag the refusal
 * was sticky ANYWAY, just by side effect: `holders` never returns to zero
 * while the sidebar bell is mounted, so `acquire` would keep taking the
 * `holders > 1` early return and never re-ask. Making it a decision means the
 * comment and the behaviour agree, and that the tab lands in exactly the
 * documented degraded mode: no exemption, everything else unchanged.
 */
let lockManagerRefused = false;

/**
 * Reported exactly once per tab, and the uniqueness comes from
 * `lockManagerRefused` rather than a latch of its own: the refusal blocks
 * every later `acquire`, so there is no second call to swallow. A second
 * latch here looked like belt-and-braces and was an unreachable branch.
 */
function reportLockManagerFailure(err: unknown): void {
  Sentry.captureException(
    err instanceof Error ? err : new Error('tab-alive lock request failed'),
    { tags: { area: 'notifications-ws' } }
  );
}

function acquire(): void {
  holders += 1;
  // Already held (or a grant is already in flight) for this tab.
  if (holders > 1) return;

  const locks = getLockManager();
  if (!locks || lockManagerRefused) return;

  const gen = ++generation;
  const controller = new AbortController();
  abortCurrent = controller;
  void locks
    .request(
      lockNameForThisTab(),
      { mode: 'exclusive', signal: controller.signal },
      () =>
        new Promise<void>((resolve) => {
          // The last holder unmounted while the grant was in flight. Resolve
          // straight away: nothing is waiting on this name, but a lock held
          // by nobody would keep this tab exempt forever, which is a promise
          // we no longer have any caller to keep.
          if (gen !== generation) {
            resolve();
            return;
          }
          releaseCurrent = resolve;
        })
    )
    .catch((err) => {
      if (gen !== generation) return;
      releaseCurrent = null;
      abortCurrent = null;
      // Nothing else ever asks for this name, so a rejection here is the lock
      // manager itself refusing — never contention, and never our own abort
      // (that path leaves `gen` behind `generation` and returned above).
      // There is no fallback to take: the tab keeps today's behaviour.
      lockManagerRefused = true;
      reportLockManagerFailure(err);
    });
}

function releaseOne(): void {
  // Paired with `acquire` by the effect below (one cleanup per run), so this
  // cannot go negative. A `Math.max(0, …)` clamp here was an unreachable
  // branch dressed as defence.
  holders -= 1;
  if (holders > 0) return;
  generation += 1;
  releaseCurrent?.();
  releaseCurrent = null;
  abortCurrent?.abort();
  abortCurrent = null;
}

/**
 * Hold this tab's freezing exemption while `active`.
 *
 * `active` is "there is a live feed connection worth protecting" — never
 * "this tab is in front".
 */
export function useTabAliveLock(active: boolean): void {
  useEffect(() => {
    if (!active) return undefined;
    acquire();
    return releaseOne;
  }, [active]);
}
